import { useMutation, useQuery } from '@tanstack/react-query'
import { apiErrorMessage, certificatesApi } from '../../api/client'
import { Award, Download, ExternalLink } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CertificatesPage() {
  const { data: certs, isLoading, error, refetch } = useQuery({
    queryKey: ['my-certs'],
    queryFn: () => certificatesApi.myCertificates().then(r => r.data),
  })

  const downloadMut = useMutation({
    mutationFn: (token: string) => certificatesApi.downloadPdf(token),
    onSuccess: ({ data }, token) => {
      const url = URL.createObjectURL(data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `packetfive-academy-certificate-${token.slice(0, 8)}.pdf`
      anchor.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    onError: (failure: unknown) => toast.error(apiErrorMessage(failure, 'Unable to download this certificate PDF. Please try again.')),
  })

  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-20 text-brand-muted">Loading…</div>
  if (error) return <div className="max-w-4xl mx-auto px-4 py-20" role="alert">{apiErrorMessage(error, 'Unable to load your certificates')} <button className="btn-outline" onClick={() => refetch()}>Retry</button></div>

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold mb-2">My Certificates</h1>
        <p className="text-brand-muted">Certificates earned for completing PacketFive Academy courses</p>
        <a className="text-brand-blue" href="https://academy.packetfive.com/certificates">View sample certificate designs</a>
      </div>

      {!certs?.length ? (
        <div className="card text-center py-16 text-brand-muted">
          <Award size={48} className="mx-auto mb-4 opacity-30"/>
          <p className="font-semibold mb-2">No certificates yet</p>
          <p className="text-sm">Complete any course to earn a verified certificate.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {certs.map(c => (
            <div key={c.id} className="card">
              <div className="flex items-start gap-3 mb-4">
                <Award size={24} className="text-amber-400 flex-shrink-0 mt-0.5"/>
                <div>
                  <h3 className="font-bold">{c.course_title}</h3>
                  <p className="text-sm text-brand-muted">Issued {new Date(c.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="btn-outline text-sm py-1.5 flex-1 justify-center" disabled={downloadMut.isPending} onClick={() => downloadMut.mutate(c.verify_token)}>
                  <Download size={14}/> {downloadMut.isPending && downloadMut.variables === c.verify_token ? 'Preparing...' : 'PDF'}
                </button>
                <a href={c.verify_url} target="_blank" rel="noopener" className="btn-ghost text-sm py-1.5 flex-1 justify-center no-underline">
                  <ExternalLink size={14}/> Verify
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
